import { Component, computed, inject, input } from '@angular/core';
import { UIIconComponent } from '@design-system';
import { AccordionComponent } from '../../../../components/accordion/accordion.component';
import { SYMBOL_DETAIL_SECTION_LABELS } from '../../../../models/symbol-detail-section.model';
import { categoryMarketUnitColumns, SymbolInfoData, symbolInfoField } from '../../../../models/symbol-info.model';
import { SymbolDetailLayoutStore } from '../../data-access/symbol-detail-layout.store';
import { NumberComponent } from '@shared-ui';

export interface SymbolInfoViewModel extends Omit<
	SymbolInfoData,
	'closingPrice' | 'feeOfPreviousDaysClosingPrice' | 'tradesCount'
> {
	closingPrice?: string | null;
	feeOfPreviousDaysClosingPrice?: string | null;
	tradesCount?: string | null;
}

@Component({
	selector: 'symbol-info',
	templateUrl: './symbol-info.component.html',
	imports: [AccordionComponent, UIIconComponent, NumberComponent],
	host: {
		class: 'block w-full'
	}
})
export class SymbolInfoComponent {
	private readonly symbolDetailLayoutStore = inject(SymbolDetailLayoutStore);

	protected readonly sectionLabel = SYMBOL_DETAIL_SECTION_LABELS['symbol-info'];

	readonly isExpanded = computed(() => this.symbolDetailLayoutStore.isExpanded('symbol-info'));

	onExpandedChange(expanded: boolean): void {
		this.symbolDetailLayoutStore.setExpanded('symbol-info', expanded);
	}

	/** Pre-formatted view data — merged from the live SSE tick and the REST fallback by the symbol-detail page. */
	readonly data = input<Partial<SymbolInfoViewModel>>();

	readonly symbolData = computed<Partial<SymbolInfoViewModel>>(() => this.data() ?? {});

	/** An unchanged closing price paints its percentage neutral; the change amount keeps its colour. */
	readonly closingPriceUnchanged = computed(() => (this.symbolData().closingPriceChangePercent ?? 0) === 0);

	template = computed(() => this.makeTemplate(this.symbolData()));

	private makeTemplate(symbol: Partial<SymbolInfoViewModel> | undefined) {
		if (!symbol || !symbol.symbolCategory) {
			return;
		}
		return categoryMarketUnitColumns.get(symbol.symbolCategory)?.rows;
	}

	getAttributeValue(row: (symbolInfoField | null | undefined)[], index: number) {
		if (row && row[index]) {
			return this.symbolData()[row[index]?.value] !== null && this.symbolData()[row[index]?.value] !== undefined
				? this.symbolData()[row[index]?.value]
				: '';
		}
		return '';
	}

	isClosingPriceRow(row: (symbolInfoField | null | undefined)[]): boolean {
		return row[0]?.value === 'closingPrice';
	}
}
